import type { Application } from 'pixi.js'
import { orientationOf, type Resizable } from './design'

/**
 * 監聽渲染器尺寸與裝置像素比變化，將所有 Resizable 場景以最新視窗大小與方向重新佈局。
 * 取代 Unity 端 ResolutionManager 的橫直屏切換職責（改為各場景自行佈局）。
 *
 * 重點：PixiJS `resizeTo: window` 以 requestAnimationFrame 延遲套用新尺寸（見 ResizePlugin），
 * 若直接在 window 'resize' 當下同步讀 `app.screen` 會讀到「舊」尺寸 → 畫面不校正。
 * 因此改訂閱 renderer 'resize'（它在尺寸套用「之後」才觸發），徹底消除時序競爭；
 * 另補上 PixiJS 未涵蓋的 `orientationchange` 與 devicePixelRatio 變更處理。
 */
export class StageScaler {
  private readonly app: Application
  private readonly scenes: readonly Resizable[]
  private dprQuery: MediaQueryList | null = null

  // renderer 完成尺寸套用（含 resizeTo 的 RAF）後重新佈局 —— 此時 app.screen 已是新值
  private readonly onRendererResize = (): void => this.layout()

  // 轉向變更：PixiJS ResizePlugin 只監聽 'resize'，行動裝置轉向不一定可靠地觸發 resize，故補上。
  // 行動瀏覽器在 orientationchange 當下 innerWidth/Height 可能尚未更新 → 延一幀再量測。
  private readonly onOrientationChange = (): void => {
    requestAnimationFrame(() => this.applyResize())
  }

  // 裝置像素比變更（拖到不同 DPI 螢幕 / 縮放瀏覽器）：renderer.resolution 不會自動跟進，需主動同步。
  private readonly onDprChange = (): void => {
    requestAnimationFrame(() => {
      this.applyResize()
      this.watchDpr() // dpr 已變，原 media query 失效，重新註冊以偵測下一次
    })
  }

  constructor(app: Application, scenes: readonly Resizable[]) {
    this.app = app
    this.scenes = scenes
    app.renderer.on('resize', this.onRendererResize)
    window.addEventListener('orientationchange', this.onOrientationChange)
    this.watchDpr()
  }

  layout(): void {
    const { width, height } = this.app.screen
    const orientation = orientationOf(width, height)
    for (const scene of this.scenes) {
      scene.resize(width, height, orientation)
    }
  }

  /**
   * 主動以最新視窗尺寸與裝置像素比重設渲染器（轉向 / DPI 變更時用）。
   * 帶第三參數 resolution：同步尺寸與裝置像素比，避免改變 DPI 後畫面模糊；
   * 完成後 renderer 會 emit 'resize' → 觸發 layout()。
   * resizeTo 為 window，故沿用 innerWidth/innerHeight（與 PixiJS ResizePlugin 量測來源一致）。
   */
  private applyResize(): void {
    const dpr = window.devicePixelRatio || 1
    this.app.renderer.resize(window.innerWidth, window.innerHeight, dpr)
  }

  /** 註冊 devicePixelRatio 變更監聽；dpr 改變後舊 query 即失效，需重新註冊以持續偵測。 */
  private watchDpr(): void {
    if (typeof window.matchMedia !== 'function') {
      return
    }
    this.dprQuery?.removeEventListener('change', this.onDprChange)
    const dpr = window.devicePixelRatio || 1
    this.dprQuery = window.matchMedia(`(resolution: ${dpr}dppx)`)
    this.dprQuery.addEventListener('change', this.onDprChange)
  }

  destroy(): void {
    this.app.renderer.off('resize', this.onRendererResize)
    window.removeEventListener('orientationchange', this.onOrientationChange)
    this.dprQuery?.removeEventListener('change', this.onDprChange)
    this.dprQuery = null
  }
}
